/**
 * Light API helpers for New Relic integration.
 * The backend derives the user from the MSAL JWT; no userId is sent.
 */
const IS_MOCK_MODE = process.env.REACT_APP_AUTH_MODE !== "ms_entra_id";
const BASE = String(process.env.REACT_APP_SPRING_BOOT_API_URL || "").replace(/\/+$/, "");

/** Returns true if a New Relic key exists for the current caller. */
export async function hasNewRelicKey(accessToken?: string): Promise<boolean> {
    if (IS_MOCK_MODE) return true;
    if (!accessToken) throw new Error("Missing access token");
    const res = await fetch(`${BASE}/api/integrations/newrelic/token`, {
        headers: { "Authorization": `Bearer ${accessToken}` },
        cache: "no-store",
    });
    if (!res.ok) throw new Error(`hasKey failed (${res.status})`);
    return res.json();
}

/** Returns raw NerdGraph JSON from the backend. Caller parses to entities. */
export async function fetchEntities(accessToken?: string): Promise<any> {
    if (!accessToken) throw new Error("Missing access token");
    const res = await fetch(`${BASE}/api/integrations/newrelic/entities`, {
        headers: { "Authorization": `Bearer ${accessToken}` },
        cache: "no-store",
    });
    if (!res.ok) throw new Error(`entities failed (${res.status})`);
    const text = await res.text();         // backend returns raw JSON string
    return JSON.parse(text);
}

export async function fetchMetricNormalizationRules(
    guid: string,
    accessToken?: string
): Promise<any> {
    if (!accessToken) throw new Error("Missing access token");

    const res = await fetch(
        `${BASE}/api/integrations/newrelic/entities/${encodeURIComponent(guid)}/metric-normalization-rules`,
        {
            headers: { Authorization: `Bearer ${accessToken}` },
            cache: "no-store",
        }
    );

    if (!res.ok) {
        throw new Error(`Failed to load metric rules (${res.status})`);
    }

    const text = await res.text();
    return JSON.parse(text);
}

/** Returns raw NRQL JSON from the backend. Caller parses to timeseries points. */
export async function fetchMetricIngest(
    guid: string,
    fromIso: string,
    toIso: string,
    rollup: string,
    accessToken?: string
): Promise<any> {
    if (IS_MOCK_MODE) {
        const now = Date.parse(toIso);
        const start = Date.parse(fromIso);
        const step = 60 * 60 * 1000; // 1h
        const series = [];
        for (let t = start; t <= now; t += step) {
            series.push({ beginTimeSeconds: Math.floor(t / 1000), results: [{ sum: Math.floor(1000 + Math.random() * 4000) }] });
        }
        return { results: [{ timeSeries: series }] };
    }
    if (!accessToken) throw new Error("Missing access token");
    const params = new URLSearchParams({
        from: fromIso,
        to: toIso,
        rollup,
    });
    const res = await fetch(`${BASE}/api/integrations/newrelic/entities/${encodeURIComponent(guid)}/metrics?` + params, {
        headers: { "Authorization": `Bearer ${accessToken}` },
        cache: "no-store",
    });
    if (!res.ok) throw new Error(`metrics failed (${res.status})`);
    const text = await res.text();         // backend returns raw JSON string
    return JSON.parse(text);
}
